import React, { useState, useCallback } from 'react';
import mainAxios from '../../../../services/mainAxios';
import moment from 'moment';
import { useAppContext } from '../../../../AppContext';
import { openBankingStatus, bankStatus } from '../../OpenBankingUtils';

export const buildProfiscStatusOptions = (statistics, t) => [
  {
    key: "workflowStatusObj",
    count: statistics?.draftOrdersNo || 0,
    multiSelect: true,
    label: t(openBankingStatus.DRAFT.label),
    value: openBankingStatus.DRAFT.value,
  },
  {
    key: "workflowStatusObj",
    count: statistics?.readyForApprovalOrdersNo || 0,
    multiSelect: true,
    label: t(openBankingStatus.READY_FOR_APPROVAL.label),
    value: openBankingStatus.READY_FOR_APPROVAL.value,
  },
  {
    key: "workflowStatusObj",
    count: statistics?.sentToBankOrdersNo || 0,
    multiSelect: true,
    label: t(openBankingStatus.SENT_TO_BANK.label),
    value: openBankingStatus.SENT_TO_BANK.value,
  },
  {
    key: "workflowStatusObj",
    count: statistics?.pendingOrdersNo || 0,
    multiSelect: true,
    label: t(openBankingStatus.PENDING.label),
    value: openBankingStatus.PENDING.value,
  },
  {
    key: "workflowStatusObj",
    count: statistics?.deletedOrdersNo || 0,
    multiSelect: true,
    label: t(openBankingStatus.DELETED.label),
    value: openBankingStatus.DELETED.value,
  },
];

export const buildBankStatusOptions = (statistics, t) => [
  {
    key: "bankStatusObj",
    count: statistics?.receivedNo || 0,
    multiSelect: true,
    label: t(bankStatus.RECEIVED.label),
    value: bankStatus.RECEIVED.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.waitingNo || 0,
    multiSelect: true,
    label: t(bankStatus.WAITING.label),
    value: bankStatus.WAITING.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.paidNo || 0,
    multiSelect: true,
    label: t(bankStatus.PAID.label),
    value: bankStatus.PAID.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.rejectedNo || 0,
    multiSelect: true,
    label: t(bankStatus.REJECTED.label),
    value: bankStatus.REJECTED.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.partiallyProcessedNo || 0,
    multiSelect: true,
    label: t(bankStatus.PARTIALLY_PROCESSED.label),
    value: bankStatus.PARTIALLY_PROCESSED.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.cancelledNo || 0,
    multiSelect: true,
    label: t(bankStatus.CANCELLED.label),
    value: bankStatus.CANCELLED.value
  },
  {
    key: "bankStatusObj",
    count: statistics?.otherStatusNo || 0,
    multiSelect: true,
    label: t(bankStatus.OTHER.label),
    value: bankStatus.OTHER.value
  },
];

export const buildCurrencyOptions = (statistics) => [
  {
    key: "accountCurrency",
    count: statistics?.lekCurrencyOrdersNo || 0,
    multiSelect: true,
    label: "Lek",
    value: "ALL",
  },
  {
    key: "accountCurrency",
    count: statistics?.usdCurrencyOrdersNo || 0,
    multiSelect: true,
    label: "USD",
    value: "USD",
  },
  {
    key: "accountCurrency",
    count: statistics?.eurCurrencyOrdersNo || 0,
    multiSelect: true,
    label: "Euro",
    value: "EUR",
  },
];

const usePayrollStatistics = ({ filters, t }) => {
  const [statistics, setStatistics] = useState();
  const [statisticsLoading, setStatisticsLoading] = useState();
  const { user: [user], selectedCompany: [selectedCompany] } = useAppContext();

  const calculateStatistics = (data) => {
    if (data?.length > 0) {
      const {
        id,
        company,
        recordDate,
        ...rest
      } = data[0];
      let stats = {};
      Object.keys(rest).forEach((key) => {
        stats[key] = data.reduce((sum, stat) => sum + stat[key], 0);
      });
      return stats;
    }
    return {};
  };

  const refreshStatistics = async () => {
    if (!(filters?.fromDate && filters?.toDate)) {
      return;
    }
    setStatisticsLoading(true);
    try {
      const res = await mainAxios.post("/salary-orders/search", {
        object: "GetSalaryOrderStatisticsV",
        fromDate: moment(filters?.fromDate).format("YYYY-MM-DD"),
        toDate: moment(filters?.toDate).format("YYYY-MM-DD"),
        username: user.username,
        company: parseInt(selectedCompany),
      });
      if (res?.data?.status) {
        let getStatistics = calculateStatistics(res.data.content);
        setStatistics(getStatistics);
      }
    } catch (error) {
      console.error("Error refreshing statistics:", error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!(filters?.fromDate && filters?.toDate)) {
      return;
    }
    refreshStatistics();
  }, [filters?.fromDate, filters?.toDate, selectedCompany]);

  const getProfiscStatusOptions = useCallback(() => buildProfiscStatusOptions(statistics, t), [statistics, t]);
  const getBankStatusOptions = useCallback(() => buildBankStatusOptions(statistics, t), [statistics, t]);
  const getCurrencyOptions = useCallback(() => buildCurrencyOptions(statistics), [statistics]);

  return {
    statistics,
    statisticsLoading,
    refreshStatistics,
    getProfiscStatusOptions,
    getBankStatusOptions,
    getCurrencyOptions,
  };
};

export default usePayrollStatistics;
